import { useMemo } from "react";
import { useSearchParams } from "react-router";
import type { FilterMenuState } from "./types";

function parseNumber(raw: string): number | undefined {
    const value = Number(raw);
    return raw.trim() === "" || !Number.isFinite(value) ? undefined : value;
}

const nonEmpty = <T,>(values: T[]): T[] | undefined => (values.length === 0 ? undefined : values);

/**
 * Ways to read a param from the URL. A value that cannot be valid, like a mistyped status or a date
 * that is not a number, reads as unset, because the API rejects some of those outright.
 */
export const urlParam = {
    text: (raw: string) => raw,
    number: parseNumber,
    numberList: (raw: string) => nonEmpty(raw.split(",").flatMap(entry => parseNumber(entry) ?? [])),
    /** A flag that is either on ("true") or unset. */
    flag: (raw: string) => (raw === "true" ? true : undefined),
    /** One value out of a fixed set. */
    oneOf:
        <TValue extends string>(allowed: readonly TValue[]) =>
        (raw: string) =>
            allowed.find(value => value === raw),
    /** A comma-separated list drawn from a fixed set, kept in the order the URL lists it. */
    listOf:
        <TValue extends string>(allowed: readonly TValue[]) =>
        (raw: string) =>
            nonEmpty(raw.split(",").filter((entry): entry is TValue => allowed.includes(entry as TValue))),
    /** A JSON value. Malformed JSON reads as unset. Pair it with write: JSON.stringify. */
    json:
        <TValue,>() =>
        (raw: string): TValue | undefined => {
            try {
                return JSON.parse(raw) as TValue;
            } catch {
                return undefined;
            }
        }
};

type ParamReader = (raw: string) => unknown;

/** A param that needs more than a reader: a custom way to write it, or an empty list that means something. */
type ParamConfig = {
    read: ParamReader;
    /** How the value is written to the URL. Defaults to String(value), which joins a list with commas. */
    write?: (value: never) => string;
    /** Keeps an emptied list in the URL as an empty param, for a list where empty means something other than unset. */
    keepEmpty?: boolean;
};

type ParamSpec = ParamReader | ParamConfig;

type ReadValue<TSpec> = TSpec extends (raw: string) => infer TValue ? TValue : TSpec extends { read: (raw: string) => infer TValue } ? TValue : never;

/** The values read from the URL: each param typed by what its reader returns, and missing when unset. */
export type FilterValues<TParams extends Record<string, ParamSpec>> = {
    [Param in keyof TParams]?: NonNullable<ReadValue<TParams[Param]>>;
};

const configOf = (spec: ParamSpec | undefined): ParamConfig | undefined => (typeof spec === "function" ? { read: spec } : spec);

/**
 * Reads a list's filters from URL params. useFilterParams reads them this way for a component; call this
 * directly where hooks can't run, such as a route loader prefetching the list.
 */
export function parseFilterParams<TParams extends Record<string, ParamSpec>>(
    config: { params: TParams },
    searchParams: URLSearchParams
): FilterValues<TParams> {
    const present = Object.keys(config.params).flatMap(key => {
        const raw = searchParams.get(key);
        if (raw === null) {
            return [];
        }
        const param = configOf(config.params[key]) as ParamConfig;
        // An empty param kept on purpose reads as an empty list rather than unset.
        if (raw === "" && param.keepEmpty === true) {
            return [[key, []]];
        }
        const value = param.read(raw);
        return value === undefined ? [] : [[key, value]];
    });
    return Object.fromEntries(present) as FilterValues<TParams>;
}

/** The filters a list keeps in the URL. A list with a sort uses useSortParams beside it. */
export function useFilterParams<TParams extends Record<string, ParamSpec>>(config: {
    /** Every param the list keeps, and how each one is read. */
    params: TParams;
}) {
    const { params } = config;
    const [searchParams, setSearchParams] = useSearchParams();

    const paramKeys = Object.keys(params);
    // Only this list's own params. Another table on the same page writing its own filters must not
    // re-parse ours and hand every consumer a new object.
    const ownParams = paramKeys.map(key => `${key}=${searchParams.get(key) ?? ""}`).join("&");

    // Re-parsing on the serialised params rather than the object keeps this stable across
    // renders where the URL has not actually changed.
    const filters = useMemo(() => parseFilterParams(config, searchParams), [ownParams]);

    const writeParams = (next: Record<string, unknown>) => {
        setSearchParams(
            prev => {
                const nextParams = new URLSearchParams(prev);
                // Explicitly pass `undefined` to unset a param; omitted keys are left untouched.
                // An emptied multi-select unsets too, so it does not linger as a blank param, unless
                // its param keeps an empty list on purpose.
                Object.entries(next).forEach(([key, value]) => {
                    const param = configOf(params[key]);
                    if (value === undefined) {
                        nextParams.delete(key);
                    } else if (Array.isArray(value) && value.length === 0) {
                        if (param?.keepEmpty === true) {
                            nextParams.set(key, "");
                        } else {
                            nextParams.delete(key);
                        }
                    } else if (param?.write !== undefined) {
                        nextParams.set(key, param.write(value as never));
                    } else {
                        nextParams.set(key, String(value));
                    }
                });
                return nextParams;
            },
            { replace: true }
        );
    };

    const setFilters: (next: FilterValues<TParams>) => void = writeParams;

    // The shared menu and chips know params only by name, so they get the untyped reads and writes.
    const menuState: FilterMenuState = {
        values: filters,
        setValues: writeParams,
        clear: keys => writeParams(Object.fromEntries(keys.map(key => [key, undefined])))
    };

    return {
        filters,
        setFilters,
        menuState
    };
}
